function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import Icon from '../../icon';
import IconButton from '../../buttons/icon-button';
import * as COLORS from '../../theme/colors';
import * as SCALE from '../../theme/scale';
import * as FONTS from '../../theme/fonts';
import { ELEVATION_MAP } from '../../theme/elevations';
import { DialogContent } from '@reach/dialog';
const PADDING_MAP = {
  '0': 0,
  xs: SCALE.size12,
  sm: SCALE.size16,
  md: SCALE.size24,
  lg: SCALE.size32
};
const getTitleColor = (color, disabled) => {
  if (disabled) {
    return COLORS.gray500;
  }
  if (color) {
    return COLORS[color];
  }
  return COLORS.gray900;
};
export const Wrapper = styled.div`
  ${({
  outline
}) => outline && `border: 1px solid ${COLORS.gray300};`}
  border-radius: ${SCALE.size4};
  ${({
  elevation
}) => elevation && `box-shadow: ${ELEVATION_MAP[elevation]};`};
  width: ${({
  width
}) => width};
  ${({
  height
}) => height ? `max-height: ${height};` : ''}
  display: flex;
  background: ${COLORS.white};
`;
export const DialogWrapper = styled(DialogContent)`
  ${({
  outline
}) => outline && `border: 1px solid ${COLORS.gray300};`}
  border-radius: ${SCALE.size4};
  width: ${({
  width
}) => width};
  max-width: ${({
  $maxWidth
}) => $maxWidth};
  ${({
  height
}) => height ? `max-height: ${height};` : ''}
  background: ${COLORS.white};
`;
const Header = styled.div`
  ${({
  underlineHeader
}) => underlineHeader && `border-bottom: 1px solid ${COLORS.gray200};`}
  align-items: center;
  color: ${COLORS.gray900};
  display: flex;
  flex-direction: row;
  padding: ${({
  padding
}) => PADDING_MAP[padding]};
  ${({
  overline
}) => overline != null && `padding-top: ${SCALE.size8};`}
  position: relative;
`;
const Overline = styled.div`
  padding-top: ${({
  padding
}) => PADDING_MAP[padding]};
  padding-left: ${({
  padding
}) => PADDING_MAP[padding]};
  font-weight: 500;
  font-size: ${SCALE.size12};
  color: ${({
  disabled
}) => disabled ? COLORS.gray500 : COLORS.gray700};
`;
const Title = styled.span`
  color: ${({
  color,
  disabled
}) => getTitleColor(color, disabled)};
  ${({
  actionIcon
}) => !!actionIcon && `padding-right: ${SCALE.size48};`}
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({
  wrapText
}) => !wrapText && 'white-space: nowrap;'}
  font-size: ${({
  size
}) => SCALE[size]};
  font-family: ${({
  type
}) => FONTS[type]};
`;
const TitleIcon = styled(Icon)`
  flex: none;
  margin-right: ${SCALE.size8};
  stroke: ${({
  color
}) => COLORS[color]};
  fill: ${({
  color,
  outlined
}) => !outlined && COLORS[color]};
  color: ${({
  color
}) => COLORS[color]};
`;
const ActionButton = styled(props => /*#__PURE__*/React.createElement(IconButton, props))`
  position: absolute;
  top: ${SCALE.size8};
  right: ${SCALE.size14};
  color: ${({
  color
}) => COLORS[color]};
  stroke: ${({
  color
}) => COLORS[color]};
  fill: ${({
  color,
  outlined
}) => !outlined && COLORS[color]};
`;
const Content = styled.div`
  width: 100%;
  position: relative;
  height: inherit;
  background: ${COLORS.white};
  ${({
  elevation,
  isModal
}) => isModal && elevation && `box-shadow: ${ELEVATION_MAP[elevation]};`};

  ${({
  hasGradientFooter
}) => hasGradientFooter && `
    &::after {
      position: absolute;
      inset: auto 0 10px 0;
      content: '';
      background: linear-gradient(to top, ${COLORS.white} 50%, rgba(255, 255, 255, 0));
      height: 72px;
    }
  `}
`;
const TitleWrapper = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-x: hidden;
`;
const Body = styled.div`
  padding: ${({
  padding
}) => PADDING_MAP[padding]};
`;
const Footer = styled.div`
  min-height: ${SCALE.size16};
  display: flex;
  width: 100%;
`;
const FooterText = styled.div`
  font-size: ${SCALE.size12};
  color: ${COLORS.gray600};
  position: absolute;
  left: ${({
  padding
}) => PADDING_MAP[padding]};
  bottom: ${({
  padding
}) => PADDING_MAP[padding]};
`;
const StatusIcon = styled(Icon)`
  stroke: ${({
  color,
  disabled
}) => disabled ? COLORS.gray500 : COLORS[color]};
  fill: ${({
  color,
  outlined,
  disabled
}) => !outlined && (disabled ? COLORS.gray500 : COLORS[color])};
  position: absolute;
  right: ${SCALE.size12};
  bottom: ${SCALE.size12};
`;
export const CardContent = ({
  title,
  titleIcon,
  titleColor,
  actionIcon,
  outlineIcons = false,
  actionHandler,
  padding = 'sm',
  underlineHeader = true,
  children,
  statusIcon,
  iconColor = 'gray800',
  actionIconSize = 16,
  titleIconSize = 16,
  titleIconColor,
  footerText,
  overline,
  titleFont = 'fontBold',
  titleSize = 'size14',
  wrapText = false,
  disabled = false,
  hasGradientFooter,
  elevation,
  hideCloseButton = false,
  isModal
}) => {
  const showHeader = title || actionIcon;
  return /*#__PURE__*/React.createElement(Content, {
    id: "card-content",
    elevation: elevation,
    isModal: isModal,
    hasGradientFooter: hasGradientFooter
  }, overline && /*#__PURE__*/React.createElement(Overline, {
    disabled,
    padding
  }, overline), showHeader && /*#__PURE__*/React.createElement(Header, {
    padding,
    underlineHeader,
    overline
  }, /*#__PURE__*/React.createElement(TitleWrapper, {
    id: "title-wrapper"
  }, titleIcon && /*#__PURE__*/React.createElement(TitleIcon, {
    "data-testid": "title-icon",
    type: titleIcon,
    color: titleIconColor || iconColor,
    outlined: outlineIcons,
    size: titleIconSize,
    "aria-hidden": true
  }), title && /*#__PURE__*/React.createElement(Title, {
    id: "dialog-heading",
    type: titleFont,
    size: titleSize,
    color: titleColor,
    wrapText,
    disabled,
    actionIcon
  }, title)), actionIcon && !hideCloseButton && /*#__PURE__*/React.createElement(ActionButton, {
    "data-testid": "action-icon-button",
    id: "action-icon-button",
    label: actionIcon,
    color: iconColor,
    outlined: outlineIcons,
    iconSize: actionIconSize,
    variant: "tertiary",
    onClick: actionHandler,
    icon: actionIcon,
    outlineIcon: outlineIcons
  })), children && /*#__PURE__*/React.createElement(Body, {
    padding
  }, children), (footerText || statusIcon) && /*#__PURE__*/React.createElement(Footer, {
    padding
  }, footerText && /*#__PURE__*/React.createElement(FooterText, {
    padding
  }, footerText), statusIcon && /*#__PURE__*/React.createElement(StatusIcon, {
    type: statusIcon,
    color: iconColor,
    outlined: outlineIcons,
    disabled
  })));
};
export const Card = /*#__PURE__*/forwardRef(({
  width = 'inherit',
  maxWidth = '650px',
  hideCloseButton = false,
  isModal,
  ...props
}, ref) => {
  const CardWrapper = isModal ? DialogWrapper : Wrapper;
  return /*#__PURE__*/React.createElement(CardWrapper, _extends({
    id: "card-wrapper",
    "data-testid": "card",
    "data-title": props.title,
    "aria-labelledby": props.ariaLabelledBy,
    $maxWidth: maxWidth,
    ref: ref,
    width: width,
    outline: props.outline,
    elevation: props.elevation,
    height: props.height
  }, isModal ? {} : props), /*#__PURE__*/React.createElement(CardContent, _extends({
    isModal: isModal,
    hideCloseButton: hideCloseButton
  }, props)));
});
export default Card;
//# sourceMappingURL=index.js.map